import { getFirestore, collection, doc, setDoc, getDocs, query, orderBy, limit, deleteDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const HistoryService = {
    /**
     * Add or Update History Item
     * Uses Subcollection: users/{uid}/history/{seriesId}
     */
    saveHistory: async (userId, seriesData, episodeId, episodeOrder) => {
        if (!userId || !seriesData.id) return;

        try {
            const historyRef = doc(db, "users", userId, "history", seriesData.id);

            // Data to save
            const payload = {
                seriesId: seriesData.id,
                seriesTitle: seriesData.title,
                thumbnail: seriesData.thumbnail || seriesData.coverImage || '',
                contentType: seriesData.type || 'webtoon',
                lastEpisodeId: episodeId,
                lastEpisodeOrder: episodeOrder || 0,
                updatedAt: serverTimestamp(),
                // Store minimal redundant data for UI speed
                isAdult: seriesData.isAdult || false
            };

            // Use setDoc merge to behave like upsert
            await setDoc(historyRef, payload, { merge: true });

        } catch (e) {
            console.error("[HistoryService] Save Failed:", e);
        }
    },

    /**
     * Get User History (Sorted by Recent)
     */
    /**
     * Get User History (Sorted by Recent) with Pagination
     */
    getHistory: async (userId, limitCount = 20, lastDoc = null) => {
        if (!userId) return { items: [], lastVisible: null };
        try {
            const { startAfter } = await import("firebase/firestore");
            let q = query(
                collection(db, "users", userId, "history"),
                orderBy("updatedAt", "desc"),
                limit(limitCount)
            );

            if (lastDoc) {
                q = query(q, startAfter(lastDoc));
            }

            const snapshot = await getDocs(q);
            return {
                items: snapshot.docs.map(d => ({ id: d.id, ...d.data() })),
                lastVisible: snapshot.docs[snapshot.docs.length - 1]
            };
        } catch (e) {
            // [FIX] Silence permission errors for guests/expired sessions to avoid console spam
            if (e.code === 'permission-denied') {
                console.warn("[HistoryService] Permission denied (Guest or Token Expired). Returning empty.");
                return { items: [], lastVisible: null };
            }
            console.error("[HistoryService] Fetch Failed:", e);
            return { items: [], lastVisible: null };
        }
    },

    /**
     * Remove Item
     */
    removeHistory: async (userId, seriesId) => {
        if (!userId || !seriesId) return;
        try {
            await deleteDoc(doc(db, "users", userId, "history", seriesId));
        } catch (e) {
            console.error("[HistoryService] Remove Failed:", e);
        }
    },

    /**
     * Lazy Migration: Checks if legacy 'readHistory' field exists and migrates it.
     * Should be called on Login or Home Load.
     */
    migrateLegacyHistory: async (userId) => {
        if (!userId) return;
        try {
            const userRef = doc(db, "users", userId);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const data = userSnap.data();
                if (data.readHistory && Object.keys(data.readHistory).length > 0) {
                    console.log("[HistoryService] Migrating Legacy History...");
                    const legacy = data.readHistory;

                    // Batch write is better, but parallel setDoc is fine for <20 items
                    const promises = Object.keys(legacy).map(seriesId => {
                        const item = legacy[seriesId];
                        return setDoc(doc(db, "users", userId, "history", seriesId), {
                            seriesId: seriesId,
                            seriesTitle: item.seriesTitle,
                            thumbnail: item.thumbnail || '',
                            contentType: item.contentType || 'webtoon',
                            lastEpisodeId: item.lastEpisodeId,
                            lastEpisodeOrder: item.lastEpisodeOrder || 0,
                            updatedAt: serverTimestamp() // Reset time or use item.updatedAt if valid date object?
                            // item.updatedAt is likely a timestamp number.
                            // serverTimestamp() is safer for sorting index.
                        }, { merge: true });
                    });

                    await Promise.all(promises);

                    // Clear legacy field to prevent re-migration
                    // Note: updateDoc with FieldValue.delete()
                    const { updateDoc, deleteField } = await import("firebase/firestore");
                    await updateDoc(userRef, { readHistory: deleteField() });
                    console.log("[HistoryService] Migration Complete.");
                }
            }
        } catch (e) {
            console.error("[HistoryService] Migration Failed:", e);
        }
    }
};
